import pluralize from "pluralize";
import { PlayerCardReview, PlayerCardReviewDisplayType, PlayerCardWidgetOwner } from "../../services/bedrock";
import _ from "lodash";
import { SOURCE_FILTER_LABELS } from "@/components/reviews/ReviewSourceFilterButtons";
import {
  AlignVerticalJustifyEnd,
  AlignVerticalJustifyStart,
  BrainCircuit,
  ContactRound,
  FilePlay,
  Link,
  Mail,
  MapPinHouse,
  Newspaper,
  Phone,
  Share2,
  SignpostBig,
  Star,
} from "lucide-react";

export const REVIEW_DISPLAY_TYPE_LABELS: Record<PlayerCardReviewDisplayType, string> = {
  FEATURED_REVIEWS: "Featured reviews",
  SPECIFIC_REVIEW: "Specific review",
  STAR_RATING: "Star rating summary",
  NPS_RATING: "NPS summary",
  SATISFACTION_RATING: "Customer satisfaction summary",
};

export const REVIEW_DISPLAY_TYPE_GROUPS: {
  value: string;
  label: string;
  options: { type: PlayerCardReviewDisplayType; title: string; description: string }[];
}[] = [
  {
    value: "REVIEWS",
    label: "Reviews",
    options: [
      {
        type: "FEATURED_REVIEWS",
        title: "Featured reviews",
        description: "Rotates through your best positive reviews.",
      },
      { type: "SPECIFIC_REVIEW", title: "Specific review", description: "Shows one review that you choose." },
    ],
  },
  {
    value: "RATING_SUMMARY",
    label: "Rating summary",
    options: [
      { type: "STAR_RATING", title: "Star rating", description: "Your average star rating across reviews." },
      {
        type: "NPS_RATING",
        title: "Net Promoter Score",
        description: "How likely your clients are to recommend you.",
      },
      {
        type: "SATISFACTION_RATING",
        title: "Customer satisfaction",
        description: "The share of your reviews that are positive.",
      },
    ],
  },
];

export const MIN_REVIEW_RESPONSES = 5;

const ratingThreshold = (minRating: number, describe: (rating: number) => string, responses: string) => ({
  minRating,
  minimum: describe(minRating),
  responses,
});

export const REVIEW_RATING_THRESHOLDS: Partial<
  Record<PlayerCardReviewDisplayType, ReturnType<typeof ratingThreshold>>
> = {
  STAR_RATING: ratingThreshold(4, rating => `${rating} stars`, "star reviews"),
  NPS_RATING: ratingThreshold(50, rating => `an NPS of ${rating}`, "NPS responses"),
  SATISFACTION_RATING: ratingThreshold(70, rating => `${rating}% satisfaction`, "reviews"),
};

export const getReviewWidgetHiddenMessage = (
  owner: Pick<PlayerCardReview, "displayType" | "reviewId" | "sourceSystems">
): string | null => {
  const threshold = REVIEW_RATING_THRESHOLDS[owner.displayType];
  if (threshold) {
    return `This widget is hidden until there are at least ${MIN_REVIEW_RESPONSES} ${threshold.responses} and the rating reaches ${threshold.minimum}.`;
  }
  if (owner.displayType === "SPECIFIC_REVIEW") {
    return owner.reviewId ? "This widget is hidden because the selected review can no longer be shown publicly." : null;
  }
  return owner.sourceSystems?.length
    ? "This widget is hidden until there are featured reviews from the selected sources."
    : "This widget is hidden until there are featured reviews. Reviews are featured when they're positive, approved for social and highly shareable.";
};

export const getCountMetricsText = (num: number, metricType?: string) => {
  var countText = num.toString();
  if (num >= 1000000) {
    countText = (num / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (num >= 1000) {
    countText = (num / 1000).toFixed(1).replace(/\.0$/, "") + "k";
  }
  if (metricType) {
    return `${countText} ${pluralize(metricType, num)}`;
  } else {
    return countText;
  }
};

export const getWidgetTitle = (widgetType: string) => {
  return (
    {
      ContentPage: "Video Page",
      SocialMedia: "Social Media",
      SmartLink: "Smart Link",
      Review: "Reviews",
    }[widgetType] || widgetType
  );
};

export const getWidgetSubtitle = (widgetOwner: PlayerCardWidgetOwner) => {
  if (widgetOwner.type === "Link") {
    return widgetOwner.url;
  } else if (widgetOwner.type === "ContentPage" || widgetOwner.type === "Blog") {
    return _.startCase(_.capitalize(widgetOwner.selectionType));
  } else if (widgetOwner.type === "Review") {
    const label = REVIEW_DISPLAY_TYPE_LABELS[widgetOwner.displayType];
    if (widgetOwner.displayType === "FEATURED_REVIEWS" && widgetOwner.sourceSystems?.length) {
      return `${label} (${widgetOwner.sourceSystems.map(source => SOURCE_FILTER_LABELS[source]).join(", ")})`;
    }
    return label;
  } else if (widgetOwner.type === "Phone") {
    return widgetOwner.phoneNumber;
  } else if (widgetOwner.type === "Email") {
    return widgetOwner.email;
  } else if (widgetOwner.type === "Address") {
    return [
      [widgetOwner.streetLine1, widgetOwner.streetLine2].filter(Boolean).join(" "),
      widgetOwner.city,
      [widgetOwner.state, widgetOwner.zip].filter(Boolean).join(" "),
    ]
      .filter(Boolean)
      .join(", ");
  } else {
    return null;
  }
};

export const getWidgetIcon = (widgetOwner: PlayerCardWidgetOwner): React.ElementType => {
  const iconMapping: Record<PlayerCardWidgetOwner["type"], React.ElementType> = {
    Link: Link,
    SocialMedia: Share2,
    ContentPage: FilePlay,
    Blog: Newspaper,
    Post: SignpostBig,
    SmartLink: BrainCircuit,
    Phone: Phone,
    Email: Mail,
    Address: MapPinHouse,
    Contact: ContactRound,
    Review: Star,
    Header: AlignVerticalJustifyStart,
    Footer: AlignVerticalJustifyEnd,
  };
  return iconMapping[widgetOwner.type];
};
